// Plain-language rollup of the register for product owners. Every figure here is derived from the same
// thresholds the engineering-facing panels use (payload.stalled_days, payload.sharp_score,
// row.due_status), so the two audiences can never read contradictory counts for the same rows.

import type { PayloadSnapshot, Row } from './types'
import { deliveredIn, deliveryMonths } from './delivery'
import { groupKey, groupLabel, unitValue } from './grouping'
import { monthStat } from './monthly'

export type HealthVerdict = 'healthy' | 'watch' | 'at-risk'

export type Direction = 'improving' | 'steady' | 'worsening'

/** The share of open work that has to be late before the whole view reads as at risk. */
export const LATE_SHARE_AT_RISK = 0.25

export type HealthPayload = Pick<PayloadSnapshot,
  'as_of' | 'stalled_days' | 'sharp_score' | 'deliv_window' | 'first_label' | 'deliv_order' | 'mbits' | 'months'
  | 'no_manager_label' | 'app_multi' | 'app_none' | 'categories' | 'no_cat_label' | 'unowned_group' | 'svp_lead'>

export interface ProductHealth {
  /** Work still open in the current view. */
  open: number
  /** Open work already past the date it was promised for. */
  late: number
  /** Open work nobody has moved for payload.stalled_days or more. */
  waiting: number
  /** Open work assessed at the highest severity the register recognises. */
  severe: number
  /** Work finished inside the recent window. */
  finished: number
  /** Open work matching any of late, waiting, or severe. Mirrors the engineering attention list. */
  attention: number
  /** Length of the recent window, in days, used for `finished`. */
  recentDays: number
  /** Number of days with no movement that counts as waiting on someone. */
  waitingDays: number
  verdict: HealthVerdict
  /** One plain-English sentence naming what drove the verdict. */
  reason: string
}

export interface InitiativeEntry {
  /** Stable identity for React keys. Not shown to the reader. */
  id: string
  /** Value the page-level filters expect for a drill-down. */
  unit: string
  /** Reader-facing initiative name. */
  name: string
  open: number
  late: number
  severe: number
  finished: number
  direction: Direction
  /** Open count in the latest month reconstructed from history. */
  latestOpen: number
  /** Open count in the month before that. */
  previousOpen: number
}

export interface DeliveryMonthCount {
  month: string
  finished: number
}

export interface DeliverySummary {
  months: DeliveryMonthCount[]
  total: number
  latestMonth: string | null
  latest: number
  previousMonth: string | null
  previous: number
  /** True when the latest month is the one still in progress, so its count is not final. */
  latestPartial: boolean
  /** latest minus previous. Positive means more work finished than in the month before. */
  change: number
  direction: Direction
}

function recentWindow(payload: Pick<PayloadSnapshot, 'deliv_window'>): number {
  const window = Number(payload.deliv_window)
  return Number.isFinite(window) && window > 0 ? window : 30
}

export function isLate(row: Row): boolean {
  return row.open && row.due_status === 'overdue'
}

export function isWaiting(row: Row, payload: Pick<PayloadSnapshot, 'stalled_days'>): boolean {
  return row.open && (row.idle || 0) >= payload.stalled_days
}

export function isSevere(row: Row, payload: Pick<PayloadSnapshot, 'sharp_score'>): boolean {
  return row.open && row.src === 'measured' && row.score !== null && row.score >= payload.sharp_score
}

// Same combination as the engineering attention list, kept in one place so the two cannot drift.
export function needsAttention(row: Row, payload: Pick<PayloadSnapshot, 'stalled_days' | 'sharp_score'>): boolean {
  return isLate(row) || isWaiting(row, payload) || isSevere(row, payload)
}

function verdictFor(open: number, late: number, waiting: number, severe: number, waitingDays: number): { verdict: HealthVerdict, reason: string } {
  if (!open) {
    return { verdict: 'healthy', reason: 'Nothing is open in this view, so there is nothing to chase.' }
  }
  const lateShare = late / open
  if (severe) {
    return {
      verdict: 'at-risk',
      reason: `${severe} of ${open} open item${open === 1 ? '' : 's'} sit at the highest severity the register tracks, which is the most serious thing here.`,
    }
  }
  if (lateShare >= LATE_SHARE_AT_RISK) {
    return {
      verdict: 'at-risk',
      reason: `${late} of ${open} open item${open === 1 ? '' : 's'} have already passed the date they were promised for, which is ${Math.round(lateShare * 100)}% of the work.`,
    }
  }
  if (late) {
    return {
      verdict: 'watch',
      reason: `${late} open item${late === 1 ? ' has' : 's have'} passed the date it was promised for, but most of the work is still on time.`,
    }
  }
  if (waiting) {
    return {
      verdict: 'watch',
      reason: `Nothing is late, but ${waiting} open item${waiting === 1 ? ' has' : 's have'} gone ${waitingDays} days or more with nobody moving ${waiting === 1 ? 'it' : 'them'}.`,
    }
  }
  return {
    verdict: 'healthy',
    reason: 'Nothing is late, nothing is stuck waiting on someone, and nothing is at the highest severity.',
  }
}

export function productHealth(rows: Row[], payload: HealthPayload): ProductHealth {
  const open = rows.filter((row) => row.open)
  const late = open.filter(isLate).length
  const waiting = open.filter((row) => isWaiting(row, payload)).length
  const severe = open.filter((row) => isSevere(row, payload)).length
  const attention = open.filter((row) => needsAttention(row, payload)).length
  const recentDays = recentWindow(payload)
  const finished = deliveredIn(rows, recentDays, payload.as_of).length
  const { verdict, reason } = verdictFor(open.length, late, waiting, severe, payload.stalled_days)
  return {
    open: open.length,
    late,
    waiting,
    severe,
    finished,
    attention,
    recentDays,
    waitingDays: payload.stalled_days,
    verdict,
    reason,
  }
}

function directionOf(latest: number, previous: number): Direction {
  if (latest < previous) return 'improving'
  if (latest > previous) return 'worsening'
  return 'steady'
}

export function initiativeReport(rows: Row[], payload: HealthPayload): InitiativeEntry[] {
  const recentDays = recentWindow(payload)
  const finishedKeys = new Set(deliveredIn(rows, recentDays, payload.as_of).map((row) => row.key))
  const monthCount = payload.months.length
  const latestIndex = monthCount - 1
  const previousIndex = monthCount - 2

  const grouped = new Map<string, Row[]>()
  rows.forEach((row) => {
    const id = groupKey(row, 'epic')
    const group = grouped.get(id)
    if (group) group.push(row)
    else grouped.set(id, [row])
  })

  return Array.from(grouped, ([id, groupRows]) => {
    const open = groupRows.filter((row) => row.open)
    const latestOpen = latestIndex >= 0 ? monthStat(groupRows, latestIndex, payload.mbits).vol : 0
    const previousOpen = previousIndex >= 0 ? monthStat(groupRows, previousIndex, payload.mbits).vol : latestOpen
    return {
      id,
      unit: unitValue(groupRows[0], 'epic', payload),
      name: groupLabel(id, 'epic', payload),
      open: open.length,
      late: open.filter(isLate).length,
      severe: open.filter((row) => isSevere(row, payload)).length,
      finished: groupRows.filter((row) => finishedKeys.has(row.key)).length,
      direction: previousIndex >= 0 ? directionOf(latestOpen, previousOpen) : 'steady',
      latestOpen,
      previousOpen,
    }
  }).sort((left, right) => (
    right.late - left.late
      || right.severe - left.severe
      || right.open - left.open
      || left.name.localeCompare(right.name)
      || left.id.localeCompare(right.id)
  ))
}

export function deliverySummary(rows: Row[], payload: HealthPayload): DeliverySummary {
  const months = deliveryMonths(rows, payload).map((month) => ({ month: month.month, finished: month.delivered }))
  const total = months.reduce((sum, month) => sum + month.finished, 0)
  const latest = months.length ? months[months.length - 1] : null
  const previous = months.length > 1 ? months[months.length - 2] : null
  const latestCount = latest ? latest.finished : 0
  const previousCount = previous ? previous.finished : 0
  return {
    months,
    total,
    latestMonth: latest ? latest.month : null,
    latest: latestCount,
    previousMonth: previous ? previous.month : null,
    previous: previousCount,
    latestPartial: !!latest && latest.month === payload.as_of.slice(0, 7),
    change: latestCount - previousCount,
    // More work finished is better here, so the comparison runs the opposite way to open counts.
    direction: previous ? directionOf(previousCount, latestCount) : 'steady',
  }
}
